import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Loader2, Calendar as CalendarIcon, Clock, CheckCircle2, Zap, Target, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import axios from 'axios';

import { API } from '@/config/api';

const QUICK_HOURS = [0.5, 1, 2, 3, 4, 5, 6, 7, 8];

const SUCCESS_MESSAGES = [
  "🎉 FATTO! Il Padrino è soddisfatto!",
  "✨ REGISTRATO! Sei un vero soldato!",
  "🏆 PERFETTO! La Famiglia è fiera!",
  "💪 BRAVO! Continua così, compare!",
];

const QuickEntry = ({ user, commesse, onEntryCreated }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedCommessa, setSelectedCommessa] = useState(null);
  const [hours, setHours] = useState('');
  const [description, setDescription] = useState('');
  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCommessa || !hours || !description) return;

    setLoading(true);
    try {
      await axios.post(`${API}/time-entries`, {
        user_id: user.id,
        user_name: user.name,
        date: format(selectedDate, 'yyyy-MM-dd'),
        commessa_code: selectedCommessa.code,
        commessa_name: selectedCommessa.name,
        project_id: null,
        project_name: projectName || null,
        description,
        hours: parseFloat(hours),
        notes: null
      });

      setSuccessMsg(SUCCESS_MESSAGES[Math.floor(Math.random() * SUCCESS_MESSAGES.length)]);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setHours('');
        setDescription('');
        setProjectName('');
        onEntryCreated();
      }, 2000);

    } catch (err) {
      console.error('Error creating entry:', err);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto text-center py-20 animate-fade-in">
        <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6 animate-bounce">
          <CheckCircle2 className="w-12 h-12 text-green-400" />
        </div>
        <h3 className="font-display text-3xl font-bold text-white mb-2">{successMsg}</h3>
        <p className="text-zinc-500">Ore registrate nel libro della Famiglia</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
          <Zap className="w-8 h-8 text-amber-400" />
        </div>
        <h2 className="font-display text-3xl font-bold text-white mb-2">REGISTRAZIONE VELOCE</h2>
        <p className="text-zinc-500">Compila i campi. Niente domande. 🤌</p>
      </div>

      {/* Form */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Date */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" />
              Data
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left h-12 bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
                  data-testid="date-picker-button"
                >
                  <CalendarIcon className="w-4 h-4 mr-2 text-zinc-400" />
                  {format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  locale={it}
                  initialFocus
                  className="bg-zinc-900"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Commessa */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
              <Target className="w-4 h-4" />
              Commessa
            </label>
            <Select
              onValueChange={(code) => setSelectedCommessa(commesse.find(c => c.code === code))}
              value={selectedCommessa?.code || ''}
            >
              <SelectTrigger className="h-12 bg-zinc-800 border-zinc-700 text-white" data-testid="commessa-select">
                <SelectValue placeholder="Seleziona commessa..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                {commesse.map((c) => (
                  <SelectItem key={c.code} value={c.code} className="text-white">
                    <span className="font-mono text-xs text-zinc-500 mr-2">{c.code}</span>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Project */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Progetto <span className="text-zinc-600">(opzionale)</span>
            </label>
            <Input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="es. Tunisia, Episodio 5..."
              className="h-12 bg-zinc-800 border-zinc-700 text-white"
              data-testid="project-input"
            />
          </div>

          {/* Hours */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Ore
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {QUICK_HOURS.map((h) => (
                <Button
                  key={h}
                  type="button"
                  variant={parseFloat(hours) === h ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setHours(h.toString())}
                  className={parseFloat(hours) === h
                    ? 'bg-primary text-black font-bold'
                    : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'
                  }
                  data-testid={`quick-hour-${h}`}
                >
                  {h}h
                </Button>
              ))}
            </div>
            <Input
              type="number"
              step="0.5"
              min="0.5"
              max="24"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="Ore lavorate"
              className="h-12 bg-zinc-800 border-zinc-700 text-white"
              data-testid="hours-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Descrizione</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Cosa hai fatto..."
              className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
              data-testid="description-input"
            />
          </div>

          {/* Submit */}
          <Button
            type="submit"
            className="w-full h-14 bg-amber-600 hover:bg-amber-500 text-white font-bold text-lg"
            disabled={loading || !selectedCommessa || !hours || !description}
            data-testid="submit-entry-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Registrando...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5 mr-2" />
                REGISTRA ORE 🤌
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default QuickEntry;
